import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';

const GRADIENT_OXFORD_BLUE = '#070D31';
const GRADIENT_AQUA_MARINE = '#6DFFBC';

interface ErrorScreenBaseProps {
    title: string;
    description: string;
    buttonText?: string;
    onButtonPress?: () => void;
}

const ErrorScreenBase: React.FC<ErrorScreenBaseProps> = ({
    title,
    description,
    buttonText,
    onButtonPress,
}) => {
    const { width } = useWindowDimensions();
    const isCompact = width < 950;

    if (Platform.OS === 'web') {
        const safePad = {
            paddingTop: 'max(16px, env(safe-area-inset-top))',
            paddingRight: 'max(20px, env(safe-area-inset-right))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
            paddingLeft: 'max(20px, env(safe-area-inset-left))',
        } as const;

        const webContainerStyle = {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            ...safePad,
            backgroundColor: GRADIENT_OXFORD_BLUE,
            backgroundImage: `linear-gradient(180deg, ${GRADIENT_OXFORD_BLUE} 0%, ${GRADIENT_AQUA_MARINE} 100%)`,
            textAlign: 'center',
            zIndex: 99999,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
        } as React.CSSProperties;

        const webInnerStyle = {
            width: '100%',
            maxWidth: Platform.isTV ? 'min(90vw, 1240px)' : 560,
            minWidth: 0,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxSizing: 'border-box',
        } as React.CSSProperties;

        const webTitleStyle = {
            color: '#FFFFFF',
            fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
            fontWeight: 700,
            fontSize: isCompact
                ? 'clamp(22px, 5.5vw, 28px)'
                : Platform.isTV
                  ? `clamp(28px, 3.6vw, ${calcSizeForDevice(64, 74)}px)`
                  : `${calcSizeForDevice(64, 74)}px`,
            lineHeight: isCompact ? 1.25 : Platform.isTV ? 1.2 : `${calcSizeForDevice(70, 80)}px`,
            margin: 0,
            maxWidth: '100%',
            alignSelf: 'stretch',
            textAlign: 'center' as const,
            overflowWrap: 'break-word' as const,
            wordBreak: 'normal' as const,
            ...(Platform.isTV && {
                textWrap: 'balance' as const,
                textShadow: '0 2px 12px rgba(7, 13, 49, 0.45)',
            }),
        } as React.CSSProperties;

        const webDescriptionStyle = {
            color: '#FFFFFF',
            opacity: Platform.isTV ? 1 : 0.85,
            fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
            fontSize: isCompact
                ? 'clamp(15px, 3.8vw, 18px)'
                : `${calcSizeForDevice(24, 27)}px`,
            lineHeight: isCompact ? 1.45 : `${calcSizeForDevice(27, 32)}px`,
            marginTop: '16px',
            marginBottom: buttonText && onButtonPress ? '24px' : 0,
            maxWidth: '100%',
            alignSelf: 'stretch',
            textAlign: 'center' as const,
            overflowWrap: 'break-word' as const,
            wordBreak: 'normal' as const,
            ...(Platform.isTV && {
                textWrap: 'balance' as const,
                textShadow: '0 1px 10px rgba(7, 13, 49, 0.4)',
            }),
        } as React.CSSProperties;

        const webButtonStyle = {
            backgroundColor: '#FFFFFF',
            borderRadius: 8,
            padding: '12px 20px',
            border: 'none',
            cursor: 'pointer',
            color: GRADIENT_OXFORD_BLUE,
            fontWeight: 600,
            fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
            alignSelf: 'center',
            maxWidth: '100%',
            boxSizing: 'border-box' as const,
            ...(Platform.isTV && {
                minHeight: `${calcSizeForDevice(44, 70)}px`,
                minWidth: `${calcSizeForDevice(120, 200)}px`,
                fontSize: `${calcSizeForDevice(18, 24)}px`,
            }),
        } as React.CSSProperties;

        return (
            <div style={webContainerStyle}>
                <div style={webInnerStyle}>
                    <h1 style={webTitleStyle}>{title}</h1>
                    <p style={webDescriptionStyle}>{description}</p>
                    {buttonText && onButtonPress ? (
                        <button
                            type="button"
                            className={Platform.isTV ? 'touchable' : undefined}
                            style={webButtonStyle}
                            onClick={onButtonPress}>
                            {buttonText}
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <LinearGradient
            colors={[GRADIENT_OXFORD_BLUE, GRADIENT_AQUA_MARINE]}
            locations={[0, 1]}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={styles.container}
        >
            <View style={styles.content}>
                <View style={styles.textContainer}>
                    <Text
                        style={[
                            styles.title,
                            {
                                fontSize: calcSizeForDevice(isCompact ? 28 : 64, 74),
                                lineHeight: calcSizeForDevice(isCompact ? 36 : 70, 80),
                                ...(Platform.isTV && {
                                    maxWidth: '88%',
                                    textShadowColor: 'rgba(7, 13, 49, 0.45)',
                                    textShadowOffset: { width: 0, height: 2 },
                                    textShadowRadius: 12,
                                }),
                            },
                        ]}
                    >
                        {title}
                    </Text>
                    <Text
                        style={[
                            styles.description,
                            {
                                fontSize: calcSizeForDevice(isCompact ? 18 : 24, 27),
                                lineHeight: calcSizeForDevice(isCompact ? 22 : 27, 32),
                                opacity: Platform.isTV ? 1 : undefined,
                                ...(Platform.isTV && {
                                    maxWidth: '90%',
                                    textShadowColor: 'rgba(7, 13, 49, 0.35)',
                                    textShadowOffset: { width: 0, height: 1 },
                                    textShadowRadius: 8,
                                }),
                            },
                        ]}
                    >
                        {description}
                    </Text>
                    {buttonText && onButtonPress ? (
                        <Pressable
                            {...(Platform.isTV
                                ? ({ useTVFocus: true, hasTVPreferredFocus: true } as object)
                                : {})}
                            style={[
                                styles.button,
                                Platform.isTV && {
                                    minHeight: calcSizeForDevice(44, 70),
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                },
                            ]}
                            onPress={onButtonPress}
                        >
                            <Text style={styles.buttonText}>{buttonText}</Text>
                        </Pressable>
                    ) : null}
                </View>
            </View>
        </LinearGradient>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        height: '100%',
    },
    content: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
        gap: 24,
    },
    textContainer: {
        alignItems: 'center',
        gap: 16,
    },
    title: {
        color: '#FFFFFF',
        fontWeight: '700',
        textAlign: 'center',
    },
    description: {
        color: '#FFFFFF',
        textAlign: 'center',
        opacity: 0.85,
    },
    button: {
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
        paddingHorizontal: 24,
        paddingVertical: 12,
    },
    buttonText: {
        color: GRADIENT_OXFORD_BLUE,
        fontWeight: '600',
        textAlign: 'center',
    },
});

export default ErrorScreenBase;
